import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Eye } from 'lucide-react';
import { FULL_COLOR } from '@customedge/shared';
import type { FabricCanvas } from '../../types/fabric';
import { useDesignStore, type DesignSide } from '../../store/designStore';
import { crossOriginForUrl } from '../../lib/crossOriginImage';

interface TemplateToolProps {
  canvas: FabricCanvas | null;
}

// Composites a template's own thumbnail over the position's real product
// photo, positioned/sized at the print area's ACTUAL location on that photo
// (areaLeft/areaTop/canvasWidth/canvasHeight, all in the photo's own natural
// pixel space — see useProductSetup.ts's side-derivation effect, which
// copies these straight from the admin-configured SelectionArea.designArea).
//
// Two things the previous version got wrong, both from treating the preview
// as "two square images stacked" instead of "a design placed on a photo":
// (1) the product photo was forced into a 1:1 square container via
// `aspect-ratio: 1` + `object-fit: cover`, cropping any non-square photo —
// percentages computed against the photo's uncropped natural size would
// then land in the wrong place on the visually cropped/scaled render; (2)
// the design thumbnail was stretched to fill the ENTIRE container via
// `object-fit: contain`, with no positioning at all relative to where the
// print area sits on the photo — so the design and the product visually had
// nothing to do with each other, which is exactly the "not aligned
// properly" / "not loading properly" bug report (both symptoms of the same
// root cause: the two layers never shared a coordinate system).
//
// Fix: size the container to the PHOTO's own natural aspect ratio (learned
// via onLoad, no forced square), so the photo never needs cropping — then
// position the design thumbnail as a percentage box computed from
// areaLeft/areaTop/canvasWidth/canvasHeight against that SAME natural size,
// so both layers share one coordinate system and the design lands exactly
// where the print area really is.
function TemplatePreviewThumb({ activeSide, thumbnailUrl, title }: { activeSide: DesignSide | undefined; thumbnailUrl: string | null; title: string }) {
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);

  const aspectRatio = naturalSize ? naturalSize.w / naturalSize.h : 1;
  const areaBox = naturalSize && activeSide
    ? {
        left:   `${(activeSide.areaLeft   / naturalSize.w) * 100}%`,
        top:    `${(activeSide.areaTop    / naturalSize.h) * 100}%`,
        width:  `${(activeSide.canvasWidth  / naturalSize.w) * 100}%`,
        height: `${(activeSide.canvasHeight / naturalSize.h) * 100}%`,
      }
    : null;

  return (
    <div style={{ position: 'relative', width: '100%', aspectRatio, background: 'var(--surface-secondary, #f1f5f9)' }}>
      {activeSide?.imagePath && (
        <img
          src={activeSide.imagePath}
          alt=""
          crossOrigin={crossOriginForUrl(activeSide.imagePath)}
          onLoad={e => setNaturalSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }}
          loading="lazy"
        />
      )}
      {thumbnailUrl && (
        // Positioned at the real print-area box once the photo's natural
        // size is known; before that (first paint), fall back to centered
        // + contained so the design is still visible rather than hidden —
        // it just corrects itself one frame later once onLoad fires.
        <img
          src={thumbnailUrl}
          alt={title}
          style={areaBox
            ? { position: 'absolute', ...areaBox, objectFit: 'fill' }
            : { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }}
          loading="lazy"
        />
      )}
    </div>
  );
}

// Opened from the small preview (Eye) icon on each template card, before the
// customer commits to applying it — gives the same composited product-photo
// + print-area-positioned design view as the card thumbnail, just at real
// screen size instead of a ~90px grid cell. Same hand-rolled modal
// convention every other modal in this app uses (ImageCompareModal.tsx,
// ImageCorrectionModal.tsx, ProductOptionsModal.tsx) — fixed dark backdrop,
// centered white card, click-outside/Escape/× all close it, no shared
// portal or modal context exists in this codebase to hook into instead.
function TemplatePreviewModal({ template, activeSide, onClose, onApply, applying }: {
  template: any;
  activeSide: DesignSide | undefined;
  onClose: () => void;
  onApply: () => void;
  applying: boolean;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div onClick={e => e.stopPropagation()} style={{
        background: 'white', borderRadius: 10, width: 'min(480px, 100%)', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
          <strong style={{ fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {template.templateTitle ?? `Template ${template.id}`}
          </strong>
          <button onClick={onClose} title={t('template.closePreview')}
            style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '2px 8px', flexShrink: 0 }}>
            ×
          </button>
        </div>

        <div style={{ padding: 16, overflow: 'auto' }}>
          <TemplatePreviewThumb activeSide={activeSide} thumbnailUrl={template.image_path ?? null} title={template.templateTitle ?? ''} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid var(--border)', background: 'none', color: 'var(--text)', cursor: 'pointer', fontSize: 13 }}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onApply}
            disabled={applying}
            className="btn btn-primary"
            style={{ fontSize: 13, opacity: applying ? 0.6 : 1 }}
          >
            {t('template.apply')}
          </button>
        </div>
      </div>
    </div>
  );
}

export function TemplateTool({ canvas }: TemplateToolProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [applying, setApplying] = useState<string | null>(null);
  const [previewTpl, setPreviewTpl] = useState<any | null>(null);
  const productId = useDesignStore(s => s.productConfig?.productId);
  const activeSide = useDesignStore(s => s.sides.find(side => side.sideKey === s.activeSideKey));
  const printPosition = activeSide?.printPosition;
  const resolveActiveColorCountKey = useDesignStore(s => s.resolveActiveColorCountKey);
  const selectedPrintAreaSizeByPosition = useDesignStore(s => s.selectedPrintAreaSizeByPosition);

  // Scoped to the product AND the print position currently active — matching
  // SKU alone used to show every template regardless of which side/position
  // the customer was actually designing; templates are now tagged by an
  // admin with exactly one printPosition (see admin Templates.tsx), so a
  // position with no active value yet (customer hasn't picked one, or this
  // side is position-agnostic) correctly shows nothing rather than every
  // template for the product.
  const { data, isFetching } = useQuery({
    queryKey: ['templates', page, productId, printPosition],
    queryFn:  () => fetch(`/api/templates?page=${page}&product_id=${productId}&print_position=${encodeURIComponent(printPosition!)}`).then(r => r.json()),
    enabled:  !!productId && !!printPosition,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  const templates: any[]  = data?.templates    ?? [];
  const totalPages: number = data?.totalPages  ?? 1;

  // Switching sides (the common in-session action, e.g. clicking a side tab)
  // changes `printPosition` without remounting this component — without
  // this, staying on page 2+ of one position's templates while switching to
  // a position whose own list has fewer pages left the pager on a page past
  // the new list's end, showing "no templates" even when page 1 has real
  // results for the newly active position.
  useEffect(() => { setPage(1); }, [productId, printPosition]);

  const applyTemplate = async (template: any) => {
    if (!canvas) return;
    setApplying(template.id);
    try {
      // A position is EITHER in color-count mode (selectedPrintColorOption /
      // selectedPrintColorByPosition) OR area-size mode
      // (selectedPrintAreaSizeByPosition, e.g. DTF Transfer's cm2 tiers) —
      // never both. Auto-recolor only makes sense in color-count mode; an
      // area-size tier has no color-count concept to recolor against, so
      // the template is applied unchanged in that case.
      const inAreaSizeMode = activeSide?.printPosition
        ? selectedPrintAreaSizeByPosition[activeSide.printPosition] != null
        : false;
      const activeColorCountKey = activeSide && !inAreaSizeMode
        ? resolveActiveColorCountKey(activeSide)
        : null;

      let canvasJson: Record<string, any> | null = null;

      // Only bother calling the recolor endpoint when there's a real,
      // resolved, non-Full-Color count to recolor against — Full Color and
      // "unresolved" both mean "use the template exactly as designed".
      if (activeColorCountKey && activeColorCountKey !== '__unset__' && activeColorCountKey !== FULL_COLOR) {
        const colorCount = Number.isFinite(Number(activeColorCountKey)) ? Number(activeColorCountKey) : activeColorCountKey;
        const recolorRes = await fetch(`/api/templates/${template.id}/recolor`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ colorCount }),
        });
        if (recolorRes.ok) {
          const recolored = await recolorRes.json();
          canvasJson = recolored.jsonObjects ?? null;
        }
      }

      if (!canvasJson) {
        // Full Color, area-size mode, or the recolor call failed/was skipped
        // — fall back to the plain detail record. The list endpoint doesn't
        // include jsonObjects (kept out of the list payload on purpose).
        // `jsonObjects` on the detail response is already a decoded plain
        // object (see backend templates.ts's GET /:id) — the same multi-side
        // map format `Design.jsonObjects` uses ({sideKey: fabricCanvasJSON}).
        const detail = await fetch(`/api/templates/${template.id}`).then(r => r.json());
        if (!detail.jsonObjects) return;
        canvasJson = detail.jsonObjects;
      }

      // Templates are tagged/listed by SKU + printPosition only (see the
      // query above) — never by print method or color variant. A position
      // can have several distinct sides (e.g. "voorzijde" split into
      // Transfer / DTF Transfer / Zeefdruk, each its own photo + sideKey),
      // and a template's stored jsonObjects key is whichever ONE of those
      // sides happened to be active when an admin originally designed it.
      // `loadFromTemplate` only paints onto a side whose sideKey has a
      // currently MOUNTED canvas (`canvasInstances.get(sideKey)`) — so
      // applying a template built on "Zeefdruk" while the customer is
      // viewing "Transfer" silently did nothing (JSON got cached, but no
      // canvas was ever found to paint it on to). Remap the template's
      // single entry onto whichever sideKey is ACTUALLY active right now,
      // so "apply" always lands on what the customer is looking at,
      // regardless of which side variant it was originally designed on —
      // matching the SKU+position-only scoping this tool already promises.
      if (activeSide && canvasJson && !(activeSide.sideKey in canvasJson)) {
        const [onlyEntry] = Object.values(canvasJson);
        if (onlyEntry) canvasJson = { [activeSide.sideKey]: onlyEntry };
      }

      useDesignStore.getState().loadFromTemplate(canvasJson!);
    } catch (err) {
      console.error('Failed to apply template:', err);
    } finally {
      setApplying(null);
    }
  };

  return (
    <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {isFetching && templates.length === 0 && (
        <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: 16 }}>
          {t('template.loading')}
        </p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
        {templates.map((tpl: any) => (
          // A plain <div role="button"> here, not a <button> — the preview
          // (Eye) icon below needs to be a REAL nested <button> so it's
          // independently keyboard/screen-reader operable, and a <button>
          // can't legally contain another <button> (invalid HTML; the
          // browser silently un-nests it, breaking click targeting). Same
          // precedent LayerPanel.tsx's row uses for its own nested "⋮" menu
          // button.
          <div
            key={tpl.id}
            role="button"
            tabIndex={0}
            onClick={() => applyTemplate(tpl)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); applyTemplate(tpl); } }}
            aria-disabled={applying === tpl.id}
            style={{
              border: '1px solid var(--border)', borderRadius: 6, background: 'none', cursor: 'pointer',
              padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column',
              opacity: applying === tpl.id ? 0.6 : 1, position: 'relative',
              pointerEvents: applying === tpl.id ? 'none' : 'auto',
            }}
            title={tpl.templateTitle ?? ''}
          >
            {/* Every template here already matches the active side's own
                printPosition (see the list query above), so they all share
                the same product photo — layering the template thumbnail
                over it, positioned at the real print area, shows exactly
                which side/position it was designed for and how it actually
                sits on the product (see TemplatePreviewThumb above). */}
            <div style={{ position: 'relative' }}>
              <TemplatePreviewThumb activeSide={activeSide} thumbnailUrl={tpl.image_path ?? null} title={tpl.templateTitle ?? ''} />
              <button
                onClick={e => { e.stopPropagation(); setPreviewTpl(tpl); }}
                title={t('template.preview')}
                aria-label={t('template.preview')}
                style={{
                  position: 'absolute', top: 4, right: 4, width: 24, height: 24,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: 'none', borderRadius: '50%', background: 'rgba(15,23,42,0.55)', color: 'white',
                  cursor: 'pointer', padding: 0,
                }}
              >
                <Eye size={14} />
              </button>
            </div>
            <span style={{ fontSize: 12, padding: '4px 6px', color: 'var(--text)', textAlign: 'left', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
              {tpl.templateTitle ?? `Template ${tpl.id}`}
            </span>
          </div>
        ))}
      </div>

      {previewTpl && (
        <TemplatePreviewModal
          template={previewTpl}
          activeSide={activeSide}
          onClose={() => setPreviewTpl(null)}
          applying={applying === previewTpl.id}
          onApply={() => { applyTemplate(previewTpl); setPreviewTpl(null); }}
        />
      )}

      {templates.length === 0 && !isFetching && (
        <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '16px 0' }}>
          {t('template.empty')}
        </p>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginTop: 4 }}>
          <button
            className="btn btn-secondary"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
            style={{ fontSize: 12, padding: '4px 10px' }}
          >
            ‹
          </button>
          <span style={{ fontSize: 12, lineHeight: '28px', color: 'var(--text-muted)' }}>
            {page} / {totalPages}
          </span>
          <button
            className="btn btn-secondary"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
            style={{ fontSize: 12, padding: '4px 10px' }}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
